import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Bell, CalendarClock, Download, ExternalLink, FileText, MapPin, Megaphone, Play } from "lucide-react";
import { Button } from "@/components/ui/button";
import { currentSlot } from "@/lib/mockStore";

/* ─────────────────────────────────────────────────────────────────── */
/*             0 · Big prominent early-bird marquee banner             */
/* ─────────────────────────────────────────────────────────────────── */
export const EarlyBirdBanner = ({
  text = "Early Bird Registration Open Till 15th November 2026",
}) => {
  if (currentSlot() !== "regular") return null;
  // Repeat the phrase enough times to fill very wide screens, then double
  // for the seamless -50% loop.
  const phrase = Array(6).fill(text);
  const loop = [...phrase, ...phrase];
  return (
    <div
      data-testid="home-earlybird-banner"
      className="relative flex items-stretch overflow-hidden border-y-2 border-[#7F1010] bg-[#D7191C] text-white shadow-[0_6px_20px_-6px_rgba(215,25,28,0.55)]"
      role="region"
      aria-label="Early bird registration announcement"
    >
      <Link
        to="/register"
        data-testid="earlybird-register-badge"
        className="relative z-20 flex shrink-0 items-center gap-2 bg-[#9E0F12] px-3 py-3 text-[11px] font-extrabold uppercase tracking-[0.18em] transition-colors hover:bg-[#7F1010] sm:px-5 sm:text-xs"
      >
        <span className="relative flex h-2.5 w-2.5">
          <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
          <span className="relative inline-flex h-2.5 w-2.5 rounded-full bg-white" />
        </span>
        <span className="hidden sm:inline">Alert</span>
        <Megaphone className="h-4 w-4 sm:hidden" aria-hidden="true" />
      </Link>
      <div className="relative flex-1 overflow-hidden py-3 sm:py-3.5">
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-10 bg-gradient-to-r from-[#D7191C] to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-[#D7191C] to-transparent" />
        <div className="earlybird-track flex min-w-max items-center gap-10 whitespace-nowrap px-4">
          {loop.map((t, idx) => (
            <Link
              to="/register"
              key={idx}
              className="flex items-center gap-10 font-display text-sm font-extrabold uppercase tracking-[0.1em] transition-colors hover:text-yellow-200 sm:text-base md:text-lg"
              data-testid={`home-earlybird-item-${idx}`}
            >
              {t}
              <span className="text-yellow-300" aria-hidden="true">★</span>
            </Link>
          ))}
        </div>
      </div>
      <Link
        to="/register"
        data-testid="earlybird-register-now-btn"
        className="relative z-20 hidden shrink-0 items-center bg-white px-5 text-xs font-bold uppercase tracking-[0.14em] text-[#B3141A] transition-colors hover:bg-yellow-100 md:flex"
      >
        Register Now
      </Link>
      <style>{`
        .earlybird-track {
          animation: apicon-earlybird 60s linear infinite;
        }
        .earlybird-track:hover { animation-play-state: paused; }
        @keyframes apicon-earlybird {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
        @media (prefers-reduced-motion: reduce) {
          .earlybird-track { animation-duration: 120s; }
        }
      `}</style>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────── */
/*                  1 · Sliding news-ticker strip                     */
/* ─────────────────────────────────────────────────────────────────── */
export const NewsTicker = ({ items = [], enabled = true }) => {
  // If the admin hasn't added anything yet, fall back to sensible defaults
  // so the banner never disappears from a live conference page.
  const DEFAULT_ITEMS = [
    { id: "d1", text: "Early-bird registration is open — save ₹1,000 on every package.", url: "", active: true },
    { id: "d2", text: "Abstract submissions accepted till 30 Nov 2026 — email assamapicon2026tinsukia@gmail.com", url: "", active: true },
    { id: "d3", text: "Conference on 18–20 Dec 2026 · Tinsukia Medical College, Assam.", url: "", active: true },
  ];
  const cmsActive = (items || []).filter((n) => n?.text && n.active !== false);
  const active = cmsActive.length ? cmsActive : DEFAULT_ITEMS;
  if (!enabled) return null;
  // Duplicate the list for a seamless marquee loop.
  const loop = [...active, ...active];
  return (
    <div
      data-testid="home-news-ticker"
      className="relative flex items-stretch overflow-hidden border-b border-apicon-navy/20 bg-apicon-navy text-white"
      role="region"
      aria-label="Site announcements"
    >
      {/* Static badge — sits OUTSIDE the marquee container with a solid
          background so scrolling text can never slide underneath it.
          Whole badge is a link to the /updates detail page. */}
      <Link
        to="/updates"
        data-testid="home-news-badge-link"
        className="relative z-20 flex shrink-0 items-center gap-2 bg-apicon-navy py-2.5 pl-4 pr-3 transition hover:bg-[#082C5A] sm:pl-6 sm:pr-4"
      >
        <span className="flex items-center gap-2 rounded-full bg-apicon-gold/25 px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.22em] text-apicon-gold">
          <Bell className="h-3 w-3" /> Updates
        </span>
      </Link>
      {/* Thin gold divider between badge and scrolling text */}
      <div className="relative z-20 w-px shrink-0 bg-apicon-gold/40" />

      {/* Marquee — the only part that scrolls */}
      <div className="relative flex-1 overflow-hidden">
        <div className="pointer-events-none absolute inset-y-0 left-0 z-10 w-10 bg-gradient-to-r from-apicon-navy to-transparent" />
        <div className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-apicon-navy to-transparent" />
        <div className="ticker-track flex h-full min-w-max items-center gap-10 whitespace-nowrap px-4 text-sm">
          {loop.map((n, idx) => {
            const inner = (
              <span className="flex items-center gap-3">
                <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-apicon-gold" />
                <span>{n.text}</span>
              </span>
            );
            const testid = `home-news-item-${idx}`;
            // Every ticker click leads to the /updates detail page (with an
            // anchor for this specific item). The optional per-item URL the
            // admin sets becomes a "Take action" button INSIDE the detail
            // card — this avoids the old 404-on-dead-URL problem entirely.
            const target = `/updates${n.id ? `#${n.id}` : ""}`;
            return (
              <Link
                key={`${n.id || idx}-${idx}`}
                to={target}
                data-testid={testid}
                className="transition hover:text-apicon-gold"
              >
                {inner}
              </Link>
            );
          })}
        </div>
      </div>
      {/* Local keyframes — kept inline so tailwind config stays untouched. */}
      <style>{`
        .ticker-track {
          animation: apicon-ticker 45s linear infinite;
        }
        .ticker-track:hover { animation-play-state: paused; }
        @keyframes apicon-ticker {
          from { transform: translateX(0); }
          to   { transform: translateX(-50%); }
        }
      `}</style>
    </div>
  );
};

/* ─────────────────────────────────────────────────────────────────── */
/*                     2 · Live event countdown                        */
/* ─────────────────────────────────────────────────────────────────── */
const useCountdown = (targetIso) => {
  const target = useMemo(() => {
    if (!targetIso) return null;
    const t = new Date(targetIso).getTime();
    return Number.isFinite(t) ? t : null;
  }, [targetIso]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!target) return;
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, [target]);
  if (!target) return null;
  const diff = target - now;
  const past = diff <= 0;
  const absMs = Math.max(0, diff);
  const days = Math.floor(absMs / (1000 * 60 * 60 * 24));
  const hours = Math.floor((absMs / (1000 * 60 * 60)) % 24);
  const minutes = Math.floor((absMs / (1000 * 60)) % 60);
  const seconds = Math.floor((absMs / 1000) % 60);
  return { past, days, hours, minutes, seconds };
};

const CountdownDigit = ({ value }) => (
  <motion.span
    key={value}
    initial={{ y: 10, opacity: 0 }}
    animate={{ y: 0, opacity: 1 }}
    transition={{ duration: 0.25, ease: "easeOut" }}
    className="inline-block font-display text-3xl font-bold tabular-nums text-white sm:text-4xl"
  >
    {String(value).padStart(2, "0")}
  </motion.span>
);

export const Countdown = ({
  enabled = true,
  eventIso = "",
  headline = "",
  compact = false,
}) => {
  const c = useCountdown(eventIso);
  if (!enabled || !c) return null;
  // Split the CMS headline around {days} so the number gets a gold-italic
  // accent. {hours} is substituted inline as plain text.
  const rawHeadline = (headline || "Only {days} days left — register now.")
    .replace("{hours}", String(c.hours));
  const [headBefore, headAfter = ""] = rawHeadline.split("{days}");

  const cells = [
    { k: "Days", v: c.days },
    { k: "Hours", v: c.hours },
    { k: "Minutes", v: c.minutes },
    { k: "Seconds", v: c.seconds },
  ];

  const eventLabel = (() => {
    const t = new Date(eventIso);
    if (!Number.isFinite(t.getTime())) return "18 – 20 Dec 2026";
    return t.toLocaleDateString("en-IN", {
      day: "numeric",
      month: "short",
      year: "numeric",
    });
  })();

  // Compact inline variant — sits under the hero CTAs. Just the countdown:
  // a small label + four mini digit cells, no progress bar, no big band.
  if (compact) {
    return (
      <div
        data-testid="home-countdown"
        className="inline-flex max-w-full flex-col gap-2.5 rounded-2xl border border-apicon-navy/10 bg-white px-4 py-3 shadow-sm"
      >
        <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em] text-apicon-navy">
          <span className="relative flex h-1.5 w-1.5">
            <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-apicon-gold opacity-75" />
            <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-apicon-gold" />
          </span>
          {c.past ? "APICON 2026 is live" : `Registration closes in · ${eventLabel}`}
        </div>
        {!c.past && (
          <p
            className="max-w-[300px] text-xs font-medium leading-snug text-apicon-navy/85"
            data-testid="home-countdown-headline"
          >
            {headBefore}
            <span className="font-semibold italic text-apicon-green">{c.days}</span>
            {headAfter}
          </p>
        )}
        <div className="flex items-stretch gap-1.5" data-testid="home-countdown-cells">
          {cells.map(({ k, v }) => (
            <div
              key={k}
              className="flex min-w-[52px] flex-col items-center rounded-lg bg-apicon-navy px-2 py-1.5"
            >
              <span className="font-display text-lg font-bold tabular-nums leading-none text-white">
                {String(v).padStart(2, "0")}
              </span>
              <span className="mt-1 text-[8px] font-semibold uppercase tracking-[0.14em] text-white/60">
                {k}
              </span>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <section
      className="relative overflow-hidden bg-apicon-navy text-white"
      data-testid="home-countdown"
    >
      {/* Atmosphere — gold glows + faint heritage pattern over deep navy */}
      <div className="pointer-events-none absolute inset-0 heritage-pattern opacity-20 invert" />
      <div className="pointer-events-none absolute -top-24 -left-24 h-[380px] w-[380px] rounded-full bg-apicon-gold/20 blur-3xl" />
      <div className="pointer-events-none absolute -bottom-32 -right-16 h-[420px] w-[420px] rounded-full bg-apicon-gold/10 blur-3xl" />

      <div className="relative mx-auto w-full max-w-7xl px-4 py-10 md:px-8 md:py-14">
        <div className="grid items-center gap-8 lg:grid-cols-12 lg:gap-12">
          {/* Left: label + headline + progress */}
          <div className="lg:col-span-6">
            <div className="inline-flex items-center gap-2 rounded-full border border-apicon-gold/40 bg-apicon-gold/10 px-3.5 py-1.5 text-[10px] font-semibold uppercase tracking-[0.22em] text-apicon-gold sm:text-[11px]">
              <span className="relative flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-apicon-gold opacity-75" />
                <span className="relative inline-flex h-2 w-2 rounded-full bg-apicon-gold" />
              </span>
              {c.past ? "The conference is live" : "Registration closing soon"}
            </div>

            <p
              className="mt-4 max-w-xl font-display text-2xl font-bold leading-snug sm:text-3xl md:text-[2.1rem]"
              data-testid="home-countdown-headline"
            >
              {c.past ? (
                "Assam APICON 2026 is live — thank you for joining us."
              ) : (
                <>
                  {headBefore}
                  <span className="italic text-apicon-gold">{c.days}</span>
                  {headAfter}
                </>
              )}
            </p>

            <div className="mt-3 flex items-center gap-2 text-sm text-white/60">
              <CalendarClock className="h-4 w-4 text-apicon-gold" />
              Assam APICON 2026 · {eventLabel} · Tinsukia Medical College
            </div>
          </div>

          {/* Right: flip-style digit cards */}
          <div className="lg:col-span-6">
            <div
              className="grid grid-cols-4 gap-2.5 sm:gap-4"
              data-testid="home-countdown-cells"
            >
              {cells.map(({ k, v }) => (
                <div
                  key={k}
                  className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5 px-2 py-4 text-center backdrop-blur-sm transition duration-300 hover:border-apicon-gold/40 hover:bg-white/10 sm:px-4 sm:py-5"
                >
                  {/* Gold hairline accent */}
                  <span className="absolute inset-x-4 top-0 h-0.5 rounded-full bg-gradient-to-r from-transparent via-apicon-gold to-transparent opacity-70" />
                  <CountdownDigit value={v} />
                  <div className="mt-1.5 text-[9px] font-semibold uppercase tracking-[0.22em] text-white/50 sm:text-[10px]">
                    {k}
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-4 text-center text-[11px] text-white/40 lg:text-right">
              {c.past
                ? "See you at the venue."
                : "Seconds roll live — this page is reading the clock as you watch."}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────────────────────────── */
/*             3 · Abstract-submission brochure callout                */
/* ─────────────────────────────────────────────────────────────────── */
export const AbstractCallout = ({
  enabled = true,
  eyebrow = "",
  title = "",
  teaser = "",
  chips = [],
  pdfUrl = "",
  pdfFilename = "APICON-2026-Abstract-Brochure.pdf",
}) => {
  if (!enabled) return null;
  // Bulletproof fallback — if the admin ever clears the PDF field from the
  // CMS, we still surface the canonical brochure so delegates can always
  // read/download the instructions and never see a naked "email" button.
  const DEFAULT_PDF =
    "https://customer-assets-rejwkqb3.emergentagent.net/job_apicon-register/artifacts/mfgnra1c_Apicon%202026%20%20%281%29.pdf";
  const effectivePdf = pdfUrl || DEFAULT_PDF;
  const DEFAULT_CHIPS = [
    { id: "c1", text: "Deadline · 30 Nov 2026" },
    { id: "c2", text: "14 categories" },
    { id: "c3", text: "Paper + E-poster tracks" },
    { id: "c4", text: "Email · assamapicon2026tinsukia@gmail.com" },
  ];
  const activeChips = ((chips && chips.length) ? chips : DEFAULT_CHIPS)
    .filter((c) => c?.text);
  return (
    <section className="relative bg-white py-10 md:py-14" data-testid="home-abstract-callout">
      <div className="mx-auto w-full max-w-7xl px-4 md:px-8">
        <motion.div
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.5 }}
          className="relative overflow-hidden rounded-3xl border border-apicon-gold/30 bg-gradient-to-br from-white via-apicon-mist/50 to-apicon-gold/10 p-6 shadow-sm md:p-10"
        >
          <div className="pointer-events-none absolute -right-16 -top-16 h-56 w-56 rounded-full bg-apicon-gold/20 blur-3xl" />
          <div className="pointer-events-none absolute -bottom-24 -left-16 h-64 w-64 rounded-full bg-apicon-navy/10 blur-3xl" />

          <div className="relative grid gap-8 lg:grid-cols-12 lg:gap-10">
            <div className="lg:col-span-7">
              <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-apicon-green md:text-xs">
                <FileText className="h-3.5 w-3.5" />
                {eyebrow || "Call for abstracts"}
              </div>
              <h2
                className="mt-2 font-display text-3xl font-bold text-apicon-navy md:text-4xl"
                data-testid="home-abstract-title"
              >
                {title}
              </h2>
              <p className="mt-4 max-w-xl text-sm leading-relaxed text-gray-700 md:text-base">
                {teaser}
              </p>
              {activeChips.length > 0 && (
                <div className="mt-5 flex flex-wrap gap-2" data-testid="home-abstract-chips">
                  {activeChips.map((c, idx) => (
                    <span
                      key={c.id || idx}
                      className="inline-flex items-center gap-1.5 rounded-full border border-apicon-navy/15 bg-white px-3 py-1 text-xs font-medium text-apicon-navy"
                    >
                      <span className="h-1.5 w-1.5 rounded-full bg-apicon-gold" />
                      {c.text}
                    </span>
                  ))}
                </div>
              )}
              <div className="mt-6 flex flex-wrap gap-3">
                <a
                  href={effectivePdf}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="home-abstract-view"
                  className="inline-flex h-11 items-center gap-2 rounded-full bg-apicon-navy px-5 text-sm font-semibold text-white transition hover:bg-[#082C5A]"
                >
                  <ExternalLink className="h-4 w-4" /> View instructions
                </a>
                <a
                  href={effectivePdf}
                  download={pdfFilename || "APICON-2026-Abstract.pdf"}
                  data-testid="home-abstract-download"
                  className="inline-flex h-11 items-center gap-2 rounded-full border border-apicon-navy/20 bg-white px-5 text-sm font-semibold text-apicon-navy transition hover:border-apicon-navy hover:bg-apicon-mist"
                >
                  <Download className="h-4 w-4" /> Download PDF
                </a>
                <Link
                  to="/register?package=pg"
                  data-testid="home-abstract-register"
                  className="inline-flex h-11 items-center gap-2 rounded-full bg-apicon-gold px-5 text-sm font-semibold text-apicon-navy shadow-md shadow-apicon-gold/40 transition hover:bg-[#ffb400]"
                >
                  Register →
                </Link>
              </div>
            </div>

            {/* Right-hand mock brochure card */}
            <div className="hidden lg:col-span-5 lg:flex lg:items-center lg:justify-center">
              <div className="relative w-full max-w-sm rotate-1 rounded-2xl border border-apicon-navy/10 bg-white p-5 shadow-xl">
                <div className="flex items-center justify-between">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-apicon-green">
                    Brochure · PDF
                  </div>
                  <FileText className="h-5 w-5 text-apicon-gold" />
                </div>
                <div className="mt-3 font-display text-xl font-bold leading-tight text-apicon-navy">
                  Assam APICON 2026
                  <br />
                  <span className="text-apicon-green">Abstract Submission</span>
                </div>
                <div className="mt-4 space-y-2">
                  {[
                    "35th Annual API Conference",
                    "18-20 Dec 2026 · Tinsukia",
                    "Deadline · 30 Nov 2026",
                    "Notifications by 15 Nov 2026",
                  ].map((line) => (
                    <div key={line} className="flex items-center gap-2 text-xs text-gray-700">
                      <span className="h-1 w-6 rounded-full bg-apicon-gold" />
                      {line}
                    </div>
                  ))}
                </div>
                <div className="mt-4 rounded-lg bg-apicon-mist p-3 text-[10px] leading-relaxed text-apicon-navy/70">
                  Paper presentation + e-poster tracks. 14 medical specialties. Delegate registration required before submission.
                </div>
              </div>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

/* ─────────────────────────────────────────────────────────────────── */
/*            4 · Explore Tinsukia — videos + photos                   */
/* ─────────────────────────────────────────────────────────────────── */
// YouTube accepts many shapes: raw 11-char ID, full watch URL, short youtu.be
// link, embed URL, shorts URL. This helper distils any of those down to the
// 11-char video ID that the /embed/ path needs.
const extractYouTubeId = (raw) => {
  if (!raw) return "";
  const trimmed = String(raw).trim();
  if (/^[A-Za-z0-9_-]{11}$/.test(trimmed)) return trimmed;
  try {
    const u = new URL(trimmed);
    if (u.hostname.includes("youtu.be")) {
      return u.pathname.split("/").filter(Boolean)[0] || "";
    }
    if (u.searchParams.get("v")) return u.searchParams.get("v");
    const parts = u.pathname.split("/").filter(Boolean);
    const idx = parts.findIndex((p) => ["embed", "shorts", "live", "v"].includes(p));
    if (idx >= 0 && parts[idx + 1]) return parts[idx + 1];
  } catch {
    /* not a URL */
  }
  const m = trimmed.match(/[A-Za-z0-9_-]{11}/);
  return m ? m[0] : "";
};

export const ExploreTinsukia = ({
  enabled = true,
  eyebrow = "",
  title = "",
  body = "",
  videos = [],
  photos = [],
}) => {
  const activeVideos = (videos || [])
    .map((v) => ({ ...v, __id: extractYouTubeId(v?.youtubeId || v?.youtube_id) }))
    .filter((v) => v.__id);
  const activePhotos = (photos || []).filter((p) => p?.url);
  if (!enabled || (activeVideos.length === 0 && activePhotos.length === 0)) {
    return null;
  }
  return (
    <section
      className="relative overflow-hidden bg-apicon-mist/40 py-14 md:py-20"
      data-testid="home-tourism"
    >
      <div className="pointer-events-none absolute inset-0 heritage-pattern opacity-40" />
      <div className="relative mx-auto w-full max-w-7xl px-4 md:px-8">
        <div className="max-w-2xl">
          <div className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.22em] text-apicon-green md:text-xs">
            <MapPin className="h-3.5 w-3.5" />
            {eyebrow || "Before & after"}
          </div>
          <h2 className="mt-2 font-display text-3xl font-bold text-apicon-navy md:text-4xl">
            {title || "Explore Tinsukia & nearby"}
          </h2>
          {body && (
            <p className="mt-3 max-w-xl text-sm text-gray-700 md:text-base">{body}</p>
          )}
        </div>

        {/* Videos row */}
        {activeVideos.length > 0 && (
          <div
            className="mt-8 grid grid-cols-1 gap-5 md:grid-cols-2"
            data-testid="home-tourism-videos"
          >
            {activeVideos.slice(0, 4).map((v, idx) => (
              <div
                key={v.id || idx}
                className="group overflow-hidden rounded-3xl border border-apicon-navy/10 bg-black shadow-sm"
              >
                <div className="relative aspect-video w-full">
                  <iframe
                    className="absolute inset-0 h-full w-full"
                    src={`https://www.youtube.com/embed/${v.__id}?rel=0&modestbranding=1`}
                    title={v.caption || "Explore Tinsukia"}
                    loading="lazy"
                    frameBorder="0"
                    allow="accelerometer; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                    data-testid={`home-tourism-video-${idx}`}
                  />
                </div>
                {v.caption && (
                  <div className="flex items-center gap-2 bg-white px-4 py-3 text-sm text-apicon-navy">
                    <Play className="h-3.5 w-3.5 text-apicon-gold" />
                    {v.caption}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        {/* Photos row */}
        {activePhotos.length > 0 && (
          <div
            className="mt-6 grid grid-cols-2 gap-3 md:mt-10 md:grid-cols-3 md:gap-4"
            data-testid="home-tourism-photos"
          >
            {activePhotos.slice(0, 6).map((p, idx) => (
              <figure
                key={p.id || idx}
                className="group relative overflow-hidden rounded-2xl border border-apicon-navy/10 bg-white shadow-sm"
              >
                <img
                  src={p.url}
                  alt={p.caption || `Tinsukia photo ${idx + 1}`}
                  className="aspect-[4/3] w-full object-cover transition duration-500 group-hover:scale-105"
                  loading="lazy"
                  data-testid={`home-tourism-photo-${idx}`}
                />
                {p.caption && (
                  <figcaption className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 to-transparent px-3 py-2 text-[11px] font-medium text-white">
                    {p.caption}
                  </figcaption>
                )}
              </figure>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};
